/**
 * Override de App.js para SaaS — añade la ruta pública /invite
 * sin modificar ningún otro comportamiento del App original.
 *
 * Cambio único: agrega <Route path="/invite" element={<InviteAccept />} />
 * como ruta pública (sin ProtectedRoute), antes de la redirección por defecto.
 *
 * El componente InviteAccept también gestiona el token pendiente cuando el
 * usuario vuelve al /invite tras iniciar sesión (sessionStorage).
 */
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import './App.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { ControlTagsCatalogProvider } from './context/ControlTagsCatalogContext';
import ProtectedRoute from './components/ProtectedRoute';

import Login from './components/Login';
import LoadingScreen from './components/LoadingScreen';

import Header from './components/Header';
import Footer from './components/Footer';
import LandingPage from './components/LandingPage';
import Index from './components/Index';
import Dashboard from './components/Dashboard';
import List from './components/List';
import Analysis from './components/Analysis';
import Reports from './components/Reports';
import CreateInformationSystem from './components/CreateInformationSystem';
import UploadDiagram from './components/UploadDiagram';
import UserManagement from './components/UserManagement';
import ProjectManager from './components/ProjectManager';
import InviteAccept from './components/InviteAccept';
import GoogleCallback from './components/GoogleCallback';
import { APP_DESCRIPTION, APP_NAME, APP_THEME_COLOR } from './config/runtime';
import { createAppTheme } from './theme/adminTheme';

const appTheme = createAppTheme();

function AppContent() {
  const { isAuthenticated, logout, isLoading } = useAuth();

  useEffect(() => {
    document.title = APP_NAME;
    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute('content', APP_DESCRIPTION);
    }

    const themeColorTag = document.querySelector('meta[name="theme-color"]');
    if (themeColorTag) {
      themeColorTag.setAttribute('content', APP_THEME_COLOR);
    }
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="App">
      {isAuthenticated && <Header onLogout={logout} />}

      <main>
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated
                ? <Navigate to="/" replace />
                : <Login onLogin={() => {}} />
            }
          />

          {/* Ruta pública — aceptar invitación (no requiere login) */}
          <Route path="/invite" element={<InviteAccept />} />

          {/* Ruta pública — callback OAuth Google */}
          <Route path="/auth/google/callback" element={<GoogleCallback />} />

          <Route
            path="/"
            element={
              isAuthenticated
                ? (
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                )
                : <LandingPage />
            }
          />

          <Route
            path="/systems"
            element={
              <ProtectedRoute>
                <Index />
              </ProtectedRoute>
            }
          />

          <Route
            path="/list"
            element={
              <ProtectedRoute>
                <List />
              </ProtectedRoute>
            }
          />

          <Route
            path="/analysis"
            element={
              <ProtectedRoute>
                <Analysis />
              </ProtectedRoute>
            }
          />

          <Route
            path="/analysis/:id"
            element={
              <ProtectedRoute>
                <Analysis />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/create"
            element={
              <ProtectedRoute>
                <CreateInformationSystem />
              </ProtectedRoute>
            }
          />

          <Route
            path="/upload/:id"
            element={
              <ProtectedRoute>
                <UploadDiagram />
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <ProjectManager />
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              isAuthenticated
                ? <Navigate to="/" replace />
                : <Navigate to="/login" replace />
            }
          />
        </Routes>
      </main>

      {isAuthenticated && <Footer />}
    </div>
  );
}

function App() {
  return (
    <ChakraProvider theme={appTheme}>
      <AuthProvider>
        <ControlTagsCatalogProvider>
          <Router>
            <AppContent />
          </Router>
        </ControlTagsCatalogProvider>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
